import { Component } from '@angular/core';
import { AmorphicService } from '../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../services/account-center/account-center.service';
import { AccountCenterSection } from './utilities/accountCenterSection.component';
import { Utils } from '../../../../common/js/Utils';
import { CoverpathControllerInterface } from '../../interfaces/controller/CoverpathControllerInterface';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { PolicyData } from '../../interfaces/PolicyData';
import { ActivatedRoute } from '@angular/router';
import { POLICY_TYPES } from '../../../../common/js/constants/policy';
import { PaymentModalType } from '../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { AlertIntent } from '@hit/coverpath-design-system';
@Component({
  selector: 'policy-summary',
  moduleId: module.id,
  templateUrl: './policy-summary.component.html',
})
export class PolicySummaryComponent extends AccountCenterSection {
  controller: CoverpathControllerInterface;
  initialized: boolean = false;
  primaryPolicyFaceAmount: string;
  secondaryPolicyFaceAmount: string;
  isWholeLife: boolean = false;
  isJointApp: boolean = false;
  term: number;
  primaryPolicyData: PolicyData;
  secondaryPolicyData: PolicyData;
  alert: {
    intent: string;
    text: string;
  };

  constructor(
    amorphicService: AmorphicService,
    accountCenterService: AccountCenterService,
    private route: ActivatedRoute
  ) {
    super(amorphicService, accountCenterService);
    this.controller = amorphicService.controller;
  }

  ngOnInit(): void {
    this.subscribeToAccountCenterService();
    const isAnnuity = this.accountCenterService.application.primaryPolicy.isAnnuity();
    if (!isAnnuity) {
      const policyId = this.accountCenterService.application.primaryPolicyWithPendingAmendment.__id__;
      this.primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(policyId as string);
      const primaryPolicyViewData = this.primaryPolicyData.getCACPaymentData();
      this.primaryPolicyFaceAmount = primaryPolicyViewData.getCACFaceAmount().toString();
      this.isWholeLife = this.primaryPolicyData.getCACIsWholeLife();
      this.isJointApp = this.primaryPolicyData.getCACIsJointApp();
      this.term = this.primaryPolicyData.getCACTerm();

      if (this.primaryPolicyData.getCACIsJointApp()) {
        const secondaryPolicyId = this.accountCenterService.application.secondaryPolicyWithPendingAmendment.__id__;
        this.secondaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(secondaryPolicyId);
        const secondaryPolicyViewData = this.secondaryPolicyData.getCACPaymentData();
        this.secondaryPolicyFaceAmount = secondaryPolicyViewData.getCACFaceAmount().toString();
      }

      if (this.route.snapshot.queryParams.submissionType) {
        const { policyType, showPaymentDate, submissionType } = this.route.snapshot.queryParams;
        const policyData = policyType === POLICY_TYPES.PRIMARY ? this.primaryPolicyData : this.secondaryPolicyData;
        const paymentData = policyData.getCACPaymentData();
        let paymentAmount, paymentDate;

        if (submissionType === PaymentModalType.PAYMENT_AUTHORIZATION) {
          paymentAmount = Utils.formatCurrency(paymentData.getCACTotalPremiumFromPolicyInfo(), 2);
          paymentDate = Utils.formatDateLong(paymentData.getCACNextPaymentDisplayDDB());
        } else if (submissionType === PaymentModalType.PAYMENT_RETRY) {
          paymentAmount = Utils.formatCurrency(paymentData.getCACFailedPaymentAmount(), 2);
          paymentDate = Utils.formatDateLong(new Date());
        }

        const generalAccountType = paymentData.getCACBankAccountClassification()?.toLowerCase();
        const accountLastFour = paymentData.getCACBankAccountLastFour();
        this.alert = {
          intent: AlertIntent.SUCCESS,
          text: `Your ${paymentAmount} premium payment is scheduled ${
            Utils.parseBoolean(showPaymentDate) ? `for ${paymentDate}` : ``
          } from your ${generalAccountType} account ending in ${accountLastFour}`,
        };
      }
    }
  }

  showAmendmentInformation() {
    const inRange =
      this.primaryPolicyData.getCACFinalOfferSentDate() && !this.primaryPolicyData.getCACfinalOfferAcceptedDate();
    return inRange && !this.primaryPolicyData.getCACIsMMDirectPolicy();
  }

  getPendingAmendmentDateFormatted() {
    if (this.primaryPolicyData.getCACHasPendingAmendment()) {
      const latestDate = this.primaryPolicyData.getCACLatestAmendmentDate();
      const latestDateSecondaryPolicy = this.secondaryPolicyData
        ? this.secondaryPolicyData.getCACLatestAmendmentDate()
        : null;
      if (latestDate && latestDate > latestDateSecondaryPolicy) {
        return Utils.dateToMonthDayTimeWithAt(latestDate);
      } else if (latestDateSecondaryPolicy && latestDateSecondaryPolicy > latestDate) {
        return Utils.dateToMonthDayTimeWithAt(latestDateSecondaryPolicy);
      }
    }
    return null;
  }

  getAgentName() {
    return CoverpathUtils.convertToTitleCase(this.primaryPolicyData.getCACAgentDisplayName());
  }

  getProductDescription(policy: PolicyData): string {
    return CoverpathUtils.getCACProductDescription(
      policy.getCACIsSanderlingPolicy(),
      policy.getCACProduct(),
      policy.getCACIsWholeLife(),
      policy.getCACTerm(),
      policy.getCACIsEcpProduct(),
      policy.getCACIsLifeBridge()
    );
  }
}
